import React, {Component} from 'react'
import { connect } from 'react-redux'
import AccountForm from 'components/account'
import {CountryActions, DocumentActions, InvestorActions, IssuerActions, UserActions, PersonKycActions, TokenActions, SnackbarActions} from 'actionsets'
import Dependent from 'containers/shared/Dependent'
import {CenteredContent} from 'containers/index'
import {mergeStatePropsAndDefaults} from 'utils'
import {Authorization} from 'utils'
import {deepSet} from 'utils'

export class AccountInfo extends Component {
  state = {
    context: {},
    dirty: false
  }

  constructor(props) {
    super(props)
    UserActions.bindActions(this, 'user')
    InvestorActions.bindActions(this, 'investor')
    IssuerActions.bindActions(this, 'issuer')
    CountryActions.bindActions(this, 'country')
    DocumentActions.bindActions(this, 'document')
    PersonKycActions.bindActions(this, 'personKyc')
    TokenActions.bindActions(this, 'token')
    SnackbarActions.bindActions(this, 'snackbar')
  }

  get accountType() {
    return this.props.match.params.accountType
  }

  get account() {
    return {
      ...this.props[this.accountType],
      ...this.state.context,
      user: this.props.user,
      country: this.props.country,
      downloads: this.downloads,
      personKycs: this.personKycs,
      accountType: this.accountType,
      metCertificationItems: this.metCertificationItems,
      limitedUpdate: !Authorization.admin && !(this.props[this.accountType].status in {draft: true, registered: true})
    }
  }

  get downloads() {
    return mergeStatePropsAndDefaults(
      this.state.context.downloads,
      this.props[this.accountType].downloads,
      this.props[this.accountType].defaultDownloads.map(d => ({...d, isDefault: true})).filter(d => d.tag === 'aml' || d.tag === this.registeredFor),
      'name'
    )
  }

  get personKycs() {
    return this.state.context.personKycs ? this.state.context.personKycs.map(kyc => {
      const {id} = this.props[this.accountType].personKycs.find(({name}) => name === kyc.name) || {}
      const role = this.registeredFor === 'individual' ? 'Individual' : (kyc.role === 'Individual' ? '' : kyc.role)
      return {...kyc, role, id}
    }) : this.props[this.accountType].personKycs
  }

  get metCertificationItems() {
    return mergeStatePropsAndDefaults(
      this.state.context.metCertificationItems,
      this.props[this.accountType].metCertificationItems,
      [], 'certificationItemId'
    )
  }

  get registeredFor() {
    return this.state.context.registeredFor || this.props[this.accountType].registeredFor
  }

  get errors() {
    const errors = this.props[`${this.accountType}Errors`]
    return {...(errors.update || {}).meta, ...(errors.createEvent || {}).meta}
  }

  get tncs() {
    return this.props[`${this.accountType}Tncs`]
  }

  get userId() {
    return this.props.match.params.id
  }

  async dependsOn() {
    await this.actions.user.show({id: this.userId})

    await Promise.all([
      this.actions[this.accountType].show({id: this.props.user.id}),
      this.actions.country.index(),
      this.actions.country.show({id: this.props.user.country.id}),
      this.actions.document.show({name: `${this.accountType}_tncs`}),
      this.actions.document.show({name: `aml_info`}),
      this.actions.document.show({name: `cert_agreement`}),
      this.actions.document.show({name: `kyc_consent`}),
    ])
  }

  cleanContext = context => {
    const {
      downloadsUpdateAt, downloadsStatus, personKycsStatus, personKycsUpdateAt, createdAt, status,
      ...rest
    } = context
    return rest
  }

  dependenciesMet() {
    return !!(
      this.tncs && this.tncs.renderedContent &&
      this.props.country
    )
  }

  handleSendKYCInstructions = async(method, person) => {
    if (this.state.dirty) {
      await this.handleSave()
    }

    const promise = person ? this.sendKycInstructions(method, person) : Promise.all(this.personKycs.map(p =>
      p.id && p.email && p.phone && p.role && p.status === 'unsent' && this.sendKycInstructions(method, p)
    ).filter(p => p))

    try {
      await promise
      this.actions.snackbar.show("Your request has been sent")
    }
    catch (ex) {
      this.actions.snackbar.error("Could not send instructions")
    }
  }

  sendKycInstructions = async(method, person) => {
    const {data: personKyc} = await this.actions.personKyc.sendInstructions({id: person.id, method})
    const updatedIndex = this.account.personKycs.findIndex(({id}) => `${id}` === `${personKyc.id}`)
    this.setState({context: deepSet(personKyc.status, `personKycs[${updatedIndex}].status`, this.account)})
  }

  handleChange = (context) => {
    console.log(context.downloads)
    if (context.downloads.some(d1 => context.downloads.some(d2 => d1 !== d2 && d1.name === d2.name)))
      this.actions.snackbar.error("You cannot add multiple documents with the same name.")
    else {
      if (context.metCertificationItems && context.metCertificationItems.length === 0)
        context.certifiedAt = null
      this.setState({
        context: this.cleanContext(context),
        dirty: true,
      })
    }
  }

  handleSave = async(event, reason) => {
    const {user, country, ...account} = this.state.context
    if (this.state.dirty) {
      try {
        await this.actions[this.accountType].update(account)
        this.actions.snackbar.show('Account Saved.')
      }
      catch(ex) {
        this.actions.snackbar.error('Could not save the account.')
        throw ex
      }

      if (this.state.context) {
        if (this.state.context.metCertificationItems)
          this.state.context.metCertificationItems.forEach(d => delete d.supportingAttachment)
        if (this.state.context.downloads)
          this.state.context.downloads.forEach(d => delete d.attachment)
      }
      this.setState({dirty: false})
    }
    if (event) {
      try {
        await this.actions[this.accountType].createEvent(this.account.userId, {name: event, reason})
        if (this.props.user.id === Authorization.user.id) {
          await this.actions.token.verify()
        }
        this.actions.snackbar.show('Account Updated.')
        if (!Authorization.admin && event === 'register') {
          this.props.history.push(`/${this.accountType}/registration_complete`)
        }
      }
      catch(ex) {
        this.actions.snackbar.error('Could not update the account.')
        throw ex
      }
    }


  }

  render = () =>
    <CenteredContent style={{paddingTop: 20}}>
      <AccountForm
        context={this.account}
        errors={this.errors}
        onChange={this.handleChange}
        onSave={this.handleSave}
        onSendKycInstructions={this.handleSendKYCInstructions}
        tncs={this.tncs}
        certAgreement={this.props.certAgreement}
        amlInfo={this.props.amlInfo}
        kycConsent={this.props.kycConsent}
        countries={this.props.countries}
      />
    </CenteredContent>
}

export default connect(({users, investors, issuers, documents, countries}) => ({
  ...users,
  ...countries,
  ...documents,
  ...issuers,
  ...investors,
  investorErrors: investors.errors,
  issuerErrors: issuers.errors,
})
)(Dependent(AccountInfo))